import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// Route guards
import AuthGuard from "@/components/auth/AuthGuard";
import PublicRoute from "@/components/auth/PublicRoute";

// Landing pages
import HomePage from "@/pages/landing/Home";
import OverviewPage from "@/pages/landing/Overview";
import ProtectionPage from "@/pages/landing/Protection";
import ShippingPage from "@/pages/landing/Shipping";
import ClaimsPage from "@/pages/landing/Claims";
import FraudPage from "@/pages/landing/Fraud";
import ReturnsPage from "@/pages/landing/Returns";
// import ScheduleDemo from "@/pages/landing/ScheduleDemo";
import BookMeeting from "@/pages/landing/BookMeeting";

// Resources
import ArticlesPage from "@/pages/resources/Articles";
import Article from "@/components/resources/ArticlePage"
import CaseStudies from "@/pages/resources/CaseStudies";

// Auth pages
import CustomerLoginPage from "@/pages/auth/CustomerLogin";
import CustomerOtpLogin from "@/pages/auth/CustomerOtpLogin";
import FindPlanPage from "@/pages/auth/FindPlanPage";
import ResetPasswordPage from "@/pages/auth/ResetPassword";
import TokenExpiredPage from "@/pages/auth/TokenExpired";

// Dashboard
import CustomerDashboard from "@/pages/dashboard/CustomerDashboard";
import MyPlans from "@/pages/customer/MyPlans";
import PlanDetailsPage from "@/pages/customer/PlanDetailsPage";
import CustomerClaimsPage from "@/pages/customer/ClaimsPage";
import ClaimDetailsPage from "@/pages/customer/ClaimDetailsPage";
import OrderHistory from "@/pages/customer/OrderHistory";
import OrderDetails from "@/pages/customer/OrderDetails";
import OrdersPage from "@/pages/customer/OrdersPage";
import OrderDetailsPage from "@/pages/customer/OrderDetailsPage";
import ShipmentPage from "@/pages/customer/ShipmentPage";
import ReturnFlowPage from "@/pages/customer/ReturnFlowPage";
import ReturnStatusPage from "@/pages/customer/ReturnStatusPage";
import ByLoginType from "./ByLoginType";
import ReturnRouter from "@/pages/customer/ReturnRouter";
import FixResolution from "@/pages/customer/FixResolution";
import DamageResolution from "@/pages/customer/DamageResolution";
import PreferenceResolution from "@/pages/customer/PreferenceResolution";
import TrackStatus from "@/pages/customer/TrackStatus";
// import Analytics from "@/pages/customer/Analytics";
import AccountPage from "@/pages/customer/AccountPage";
import HelpSupportPage from "@/pages/customer/HelpSupportPage";
import GstInvoicesPage from "@/pages/customer/gst/GstInvoicesPage";
import GstDetailsPage from "@/pages/customer/gst/GstDetailsPage";
import TaxInvoicePage from "@/pages/customer/gst/TaxInvoicePage";
import NotificationsPage from "@/pages/customer/NotificationsPage";
import NotificationSettingsPage from "@/pages/customer/NotificationSettingsPage";

// Chat
import ChatBot from "@/pages/chat/Chatbot";
import UrbanJungleCustomer from "@/pages/customer/UrbanJungleCustomer";

// Docs
import Disclaimer from "@/pages/documentation/Disclaimer";
import Terms from "@/pages/documentation/Terms";
import Privacy from "@/pages/documentation/PrivacyPolicy";

// 404
import NotFound from "@/components/landing/layout/NotFound";

// Client FAQs
import LovePangolinCare from "@/pages/clientsFAQ/LovePangolinCare/lovepangolincare";

import UrbanFluxCare from "@/pages/clientsFAQ/UrbanFlux";
import TNCflux from "@/pages/clientsFAQ/UrbanFlux/TNCFlux"

import TheBeachCompany from "@/pages/clientsFAQ/TheBeachCompany"
import TNCbeach from "@/pages/clientsFAQ/TheBeachCompany/tnc"

import Mecturing from "@/pages/clientsFAQ/Mecturin"
import TNCMecturing from "@/pages/clientsFAQ/Mecturin/TermsNCondition"

import AutoFurnish from "@/pages/clientsFAQ/AutoFurnish"
import TNCAutoFurnish from "@/pages/clientsFAQ/AutoFurnish/TermsNCondition"

// Shipping FAQ
import ShippingProtection from "@/pages/clientsFAQ/ShippingFAQ";
import ShippingReadMore from "@/pages/clientsFAQ/ShippingFAQ/ReadMore"

import ScrollToTop from "./ScrollToTop";
import CardPage from "@/components/resources/Case";

// Contact Routes
import ContactUs from "@/pages/contact/ContactUs";
import ContactForm from "@/pages/contact/ContactForm";
import Izicart from "@/pages/clientsFAQ/izicart";
import IzicartTnc from "@/pages/clientsFAQ/izicart/TermsNCondition";
import IziExtenedWarranty from "@/pages/clientsFAQ/izicart/ExtendedWarranty";
import IziExtenedWarrantyTnc from "@/pages/clientsFAQ/izicart/ExtendedWarranty/TermsAndConditions";
import AdivaaAccidental from "@/pages/clientsFAQ/Adivaa/AdivaaAccidental";
import AdivaaAccidentalTnc from "@/pages/clientsFAQ/Adivaa/AdivaaAccidental/TermsAndConditions";
import NestrootAccidental from "@/pages/clientsFAQ/Nestroot/NestrootAccidental";
import NestrootAccidentalTnc from "@/pages/clientsFAQ/Nestroot/NestrootAccidental/TermsAndConditions";
import EumeAccidental from "@/pages/clientsFAQ/EUME/EumeAccidental";
import EumeAccidentalTnc from "@/pages/clientsFAQ/EUME/EumeAccidental/TermsAndConditions";
import ContactAddress from "@/pages/contact/ContactAddress";
import ReturnLogin from "@/pages/auth/ReturnLogin";
import FreeServiceInteraction from "@/pages/chat/FreeServiceInteraction";
import ReturnChatPage from "@/pages/customer/ReturnChatPage";
import ScanQRPage from "@/pages/customer/ScanQRPage";
import MagicPolicyPage from "@/pages/customer/MagicPolicyPage";
import TrackOrders from "@/pages/customer/TrackOrders";
// Local-dev-only demo session. `import.meta.env.DEV` is false in every `vite build`,
// so this import and its route are removed from production bundles.
const DemoLogin = import.meta.env.DEV ? lazy(() => import("@/pages/dev/DemoLogin")) : null;

const AppRoutes: React.FC = () => {
  return (
    <AnimatePresence mode="wait">
      <ScrollToTop />
      <Routes>
        {DemoLogin && (
          <Route path="/dev/demo-login" element={<Suspense fallback={null}><DemoLogin /></Suspense>} />
        )}
        <Route path="/customer/urban-jungle/*" element={<UrbanJungleCustomer />} />
        <Route path="/customer/scan-qr/:merchantId/*" element={<ScanQRPage />} />
        <Route path="/customer/magiclink" element={<MagicPolicyPage />} />
        {/* Legal pages and merchant plan terms: open to everyone, signed in or not (the
            dashboard links customers to their merchant's terms). */}
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/mecturing-productprotection/tnc" element={<TNCMecturing />} />
        <Route path="/auto-furnish-productprotection/tnc" element={<TNCAutoFurnish />} />
        <Route path="/urbanFlux/tnc" element={<TNCflux />} />
        <Route path="/thebeachcompany/tnc" element={<TNCbeach />} />
        <Route path="/izicart/tnc" element={<IzicartTnc />} />
        <Route path="/izicart/extended/tnc" element={<IziExtenedWarrantyTnc />} />
        <Route path="/adivaa/accidental/tnc" element={<AdivaaAccidentalTnc />} />
        <Route path="/nestroots/accidental/tnc" element={<NestrootAccidentalTnc />} />
        <Route path="/eume/accidental/tnc" element={<EumeAccidentalTnc />} />
        {/* Public routes */}
        <Route element={<PublicRoute />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/protection" element={<ProtectionPage />} />
          <Route path="/shipping" element={<ShippingPage />} />
          <Route path="/claims" element={<ClaimsPage />} />
          <Route path="/fraud" element={<FraudPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/schedule-demo" element={<BookMeeting />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:id" element={<Article />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:id" element={<CardPage />} />
          <Route path="/customer-login" element={<CustomerOtpLogin />} />
          <Route path="/customer-login/find-plan" element={<FindPlanPage />} />
          {/* Email + password sign-in, kept while the OTP backend is pending */}
          <Route path="/customer-login/email" element={<CustomerLoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/token-expired" element={<TokenExpiredPage />} />
          <Route path="/track-customer" element={<TrackOrders />} />

          {/* Contact Page */}
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/contact-form" element={<ContactForm />} />
          <Route path="/contact-address" element={<ContactAddress />} />
          <Route path="/:merchantName/return" element={<ReturnLogin />} />

          {/* Shipping FAQ */}
          <Route path="/shipping-protection-plancoverage" element={<ShippingProtection />} />
          <Route path="/shipping-protection-plancoverage/read-more" element={<ShippingReadMore />} />

          {/* Clients FAQ Pages */}

          {/* Mecturin */}
          <Route path="/mecturing-productprotection" element={<Mecturing />} />

          {/* Auto Furnish */}
          <Route path="/auto-furnish-productprotection" element={<AutoFurnish />} />

          {/* LovePangolinCare */}
          <Route path="/lovepangolincare" element={<LovePangolinCare />} />

          {/* UrbanFlux */}
          <Route path="/urbanFlux" element={<UrbanFluxCare />} />

          {/* UrbanFlux */}
          <Route path="/thebeachcompany" element={<TheBeachCompany />} />

          {/* izi cart */}
          <Route path="/izicart" element={<Izicart />} />
          <Route path="/izicart/extended" element={<IziExtenedWarranty />} />

          {/* adivaa accidental */}
          <Route path="/adivaa/accidental" element={<AdivaaAccidental />} />

          {/* nestroot accidental */}
          <Route path="/nestroots/accidental" element={<NestrootAccidental />} />

          {/* eume accidental */}
          <Route path="/eume/accidental" element={<EumeAccidental />} />



          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Protected customer routes */}
        <Route element={<AuthGuard />}>
          <Route path="/customer" element={<CustomerDashboard />}>
            <Route index element={<Navigate to="policies" replace />} />
            <Route path="policies" element={<MyPlans />} />
            <Route path="policies/:id" element={<PlanDetailsPage />} />
            {/* Guests (return-exchange sessions) keep the original pages, which run the return flow. */}
            <Route path="orders" element={<ByLoginType customer={<OrdersPage />} guest={<OrderHistory />} />} />
            {/* <Route path="orders/track/track-status" element={<TrackStatus />} /> */}
            <Route path="orders/:id" element={<ByLoginType customer={<OrderDetailsPage />} guest={<OrderDetails />} />} />
            <Route path="orders/:id/shipment" element={<ShipmentPage />} />
            <Route path="orders/:id/return" element={<ReturnFlowPage />} />
            <Route path="orders/:id/return/status" element={<ReturnStatusPage />} />
            <Route path="orders/:id/fix-issue" element={<ReturnRouter />} />
            <Route path="orders/:id/resolve-fix" element={<FixResolution />} />
            <Route path="orders/:id/resolve-damage" element={<DamageResolution />} />
            <Route path="orders/:id/resolve-preference" element={<PreferenceResolution />} />

            {/* <Route path="orders/:id/resolve-preference" element={<PreferenceResolution />} /> */}
            <Route path="orders/:id/track-status" element={<TrackStatus />} />
            <Route path="claims" element={<CustomerClaimsPage />} />
            <Route path="claims/:id" element={<ClaimDetailsPage />} />
            {/* <Route path="analytics" element={<Analytics />} /> */}
            <Route path="settings" element={<AccountPage />} />
            <Route path="settings/gst" element={<GstInvoicesPage />} />
            <Route path="settings/notifications" element={<NotificationSettingsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="settings/gst/edit" element={<GstDetailsPage />} />
            <Route path="settings/gst/invoices/:invoiceId" element={<TaxInvoicePage />} />
            <Route path="support" element={<HelpSupportPage />} />
          </Route>
          <Route path="/customer/chat" element={<ChatBot />} />
          <Route path="/customer/chat/:claimId" element={<ChatBot />} />
          <Route path="/customer/free-service" element={<FreeServiceInteraction />} />
          <Route path="/customer/return-chat/:orderId" element={<ReturnChatPage />} />
        </Route>

      </Routes>
    </AnimatePresence>
  );
};

export default AppRoutes;
